import React from "react";
import { Post } from "@/core/types/postTypes";
import { useAppStore } from "@/core/store/useAppStore";
import { MinimalPostCard } from "@/domains/PostCreator/cards/MinimalPostCard";

interface PostGridProps {
  posts: Post[];
  selectedPost: Post | null;
  onPostSelect: (post: Post) => void;
  isLoading: boolean;
  error?: string | null;
}

export const PostGrid: React.FC<PostGridProps> = ({
  posts,
  selectedPost,
  onPostSelect,
  isLoading,
  error,
}) => {
  const { selectedClient } = useAppStore();

  if (isLoading && posts.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading posts...</p>
        </div>
      </div>
    );
  }

  if (error && posts.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center text-red-600">
          <p className="mb-2">Failed to load posts</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center text-gray-500">
          <p className="text-lg font-medium">No posts available for grid</p>
          <p className="text-sm text-gray-400 mt-1">
            Create posts with photos or reels to add them to your grid
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3 sm:gap-4">
      {posts.map((post) => (
        <div
          key={post.id}
          onClick={() => onPostSelect(post)}
          className={`cursor-pointer transition-all duration-200 ${
            selectedPost?.id === post.id
              ? "ring-2 ring-blue-500 ring-offset-2"
              : ""
          }`}
        >
          <MinimalPostCard post={post} />
        </div>
      ))}
    </div>
  );
};
